import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PhotoUploadArea, type PhotoFile } from "./photo-upload-area";
import { MomentHeader } from "./moment-header";
import { useHubStore, type DraftMomentPhoto } from "../use-hub-store";
import { MentorshipDetails } from "@/lib/api/mentorships";

interface CreateMomentViewProps {
  mentorship: MentorshipDetails;
  onPreview?: () => void;
}

export function CreateMomentView({
  onPreview,
}: CreateMomentViewProps) {
  const draftMoment = useHubStore((state) => state.draftMoment);
  const setDraftMoment = useHubStore((state) => state.setDraftMoment);
  const navigateToView = useHubStore((state) => state.navigateToView);

  // Initialize from local draft if available
  const [photos, setPhotos] = useState<PhotoFile[]>(() => {
    if (draftMoment?.photos) {
      return draftMoment.photos.map((p) => ({
        id: p.id,
        file: p.file,
        preview: p.preview,
      }));
    }
    return [];
  });
  const [caption, setCaption] = useState(draftMoment?.caption ?? "");

  const handlePhotosChange = useCallback((newPhotos: PhotoFile[]) => {
    setPhotos(newPhotos);
  }, []);

  const canContinue = photos.length > 0;

  const handleContinue = () => {
    // Store photos and caption in hub store for preview
    const draftPhotos: DraftMomentPhoto[] = photos.map((p) => ({
      id: p.id,
      file: p.file,
      preview: p.preview,
    }));
    setDraftMoment({ photos: draftPhotos, caption });
    navigateToView("preview-moment");
    onPreview?.();
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <MomentHeader
        title="Share a moment"
        subtitle="You can upload up to 6 photos"
        showBackButton={false}
        showInfoButton
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Photo Upload Area */}
          <PhotoUploadArea photos={photos} onPhotosChange={handlePhotosChange} />

          {/* Caption Input */}
          {photos.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Add a caption (optional)
              </label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write something about your photos..."
                rows={3}
                className="resize-none"
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white p-4">
        <Button
          onClick={handleContinue}
          disabled={!canContinue}
          className="w-full rounded-full bg-black text-white hover:bg-gray-800"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
